<template>
    <div class="min-h-screen bg-slate-50/70">
        <ModalMessage
            :visible="showMessage"
            :title="messageTitle"
            :messages="messageTexts"
            @close="showMessage = false"
        />

        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6 space-y-6">
            <div class="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div class="px-6 py-6 bg-gradient-to-br from-white via-slate-50 to-blue-50">
                    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                        <div class="flex items-start gap-4">
                            <span class="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-700 shadow-sm">
                                <v-icon name="hi-solid-user-group" class="w-6 h-6" />
                            </span>

                            <div>
                                <div class="flex items-center gap-2 flex-wrap">
                                    <h1 class="text-2xl font-bold tracking-tight text-slate-900">
                                        Reparti Attività
                                    </h1>

                                    <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 ring-1 ring-blue-100">
                                        <v-icon name="hi-solid-cog" class="w-4 h-4" />
                                        Configurazione
                                    </span>
                                </div>

                                <p class="text-sm text-slate-500 mt-1 max-w-2xl">
                                    Crea reparti, assegna utenti e definisci i manager responsabili delle attività.
                                </p>
                            </div>
                        </div>

                        <button
                            @click="openCreateModal"
                            class="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-semibold shadow-sm transition-colors"
                        >
                            <v-icon name="hi-plus" class="w-4 h-4" />
                            Nuovo reparto
                        </button>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-6">
                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-xs font-medium text-slate-500">Totale reparti</p>
                                    <p class="text-xl font-bold text-slate-900 mt-0.5">{{ totalDepartments }}</p>
                                </div>
                                <span class="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                    <v-icon name="hi-solid-collection" class="w-6 h-6" />
                                </span>
                            </div>
                        </div>

                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-xs font-medium text-slate-500">Reparti attivi</p>
                                    <p class="text-xl font-bold text-emerald-700 mt-0.5">{{ activeDepartments }}</p>
                                </div>
                                <span class="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                    <v-icon name="hi-solid-check-circle" class="w-6 h-6" />
                                </span>
                            </div>
                        </div>

                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-xs font-medium text-slate-500">Utenti associati</p>
                                    <p class="text-xl font-bold text-slate-900 mt-0.5">{{ totalAssignedUsers }}</p>
                                </div>
                                <span class="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                    <v-icon name="hi-solid-users" class="w-6 h-6" />
                                </span>
                            </div>
                        </div>

                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-xs font-medium text-slate-500">Manager</p>
                                    <p class="text-xl font-bold text-amber-700 mt-0.5">{{ totalManagers }}</p>
                                </div>
                                <span class="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                                    <v-icon name="hi-solid-star" class="w-6 h-6" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="loading" class="bg-white border border-slate-200 rounded-2xl shadow-sm p-16">
                <div class="flex flex-col items-center justify-center text-center">
                    <div class="animate-spin rounded-full h-9 w-9 border-b-2 border-blue-600"></div>
                    <p class="text-sm text-slate-500 mt-4">Caricamento reparti in corso...</p>
                </div>
            </div>

            <div v-else-if="accessDenied" class="bg-white border border-orange-200 rounded-2xl shadow-sm p-10 text-center">
                <div class="w-16 h-16 rounded-2xl bg-orange-50 text-orange-500 flex items-center justify-center mx-auto">
                    <v-icon name="hi-solid-shield-check" class="w-6 h-6" />
                </div>
                <p class="text-orange-800 mt-4 font-semibold">Non hai i permessi per gestire i reparti.</p>
                <p class="text-orange-600 text-sm mt-1">Questa sezione è riservata agli amministratori.</p>
            </div>

            <div v-else-if="loadError" class="bg-white border border-red-200 rounded-2xl shadow-sm p-10 text-center">
                <div class="w-16 h-16 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mx-auto">
                    <v-icon name="hi-solid-exclamation-circle" class="w-6 h-6" />
                </div>
                <p class="text-red-800 mt-4 font-semibold">{{ loadError }}</p>
                <button
                    @click="fetchDepartments"
                    class="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 text-red-700 text-sm font-semibold hover:bg-red-100 transition-colors"
                >
                    <v-icon name="hi-refresh" class="w-4 h-4" />
                    Riprova
                </button>
            </div>

            <div v-else class="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
                <div class="space-y-3">
                    <div class="flex items-center justify-between">
                        <div>
                            <h2 class="text-sm font-bold text-slate-800">Elenco reparti</h2>
                            <p class="text-xs text-slate-500">Seleziona un reparto per gestire gli utenti.</p>
                        </div>
                    </div>

                    <div
                        v-if="departments.length === 0"
                        class="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center shadow-sm"
                    >
                        <div class="w-16 h-16 rounded-2xl bg-slate-50 text-slate-300 flex items-center justify-center mx-auto">
                            <v-icon name="bi-diagram-3" class="w-6 h-6" />
                        </div>
                        <p class="text-slate-700 font-semibold mt-4">Nessun reparto creato</p>
                        <p class="text-slate-500 text-sm mt-1">
                            Crea il primo reparto per iniziare ad associare utenti e manager.
                        </p>
                        <button
                            @click="openCreateModal"
                            class="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
                        >
                            <v-icon name="hi-plus" class="w-4 h-4" />
                            Crea il primo reparto
                        </button>
                    </div>

                    <div
                        v-for="dept in departments"
                        :key="dept.id"
                        @click="selectDepartment(dept)"
                        class="group relative bg-white border rounded-2xl p-4 cursor-pointer transition-all duration-200 overflow-hidden"
                        :class="selectedDept && selectedDept.id === dept.id
                            ? 'border-blue-300 shadow-lg shadow-blue-100/60 ring-2 ring-blue-100'
                            : 'border-slate-200 hover:border-blue-200 hover:shadow-md'"
                    >
                        <div
                            v-if="selectedDept && selectedDept.id === dept.id"
                            class="absolute left-0 top-4 bottom-4 w-1 rounded-r-full bg-blue-600"
                        ></div>

                        <div class="flex items-start gap-3">
                            <div class="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 text-indigo-700 flex items-center justify-center flex-none ring-1 ring-indigo-100">
                                <v-icon name="bi-diagram-3" class="w-6 h-6" />
                            </div>

                            <div class="min-w-0 flex-1">
                                <div class="flex items-start justify-between gap-2">
                                    <div class="min-w-0">
                                        <div class="flex items-center gap-2 min-w-0">
                                            <h3 class="font-semibold text-slate-900 truncate">
                                                {{ dept.name }}
                                            </h3>

                                            <span
                                                class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold flex-none"
                                                :class="dept.is_active
                                                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                                                    : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200'"
                                            >
                                                {{ dept.is_active ? 'Attivo' : 'Inattivo' }}
                                            </span>
                                        </div>

                                        <p class="text-sm text-slate-500 mt-1 line-clamp-2">
                                            {{ dept.description || 'Nessuna descrizione inserita.' }}
                                        </p>
                                    </div>

                                    <div class="flex items-center gap-1 flex-none opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" @click.stop>
                                        <button
                                            @click="openEditModal(dept)"
                                            class="p-2 text-slate-400 hover:text-blue-700 hover:bg-blue-50 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            title="Modifica reparto"
                                            aria-label="Modifica reparto"
                                        >
                                            <v-icon name="hi-solid-pencil" class="w-4 h-4" />
                                        </button>
                                        <button
                                            v-if="dept.is_active"
                                            @click="openConfirmDeactivate(dept)"
                                            class="p-2 text-slate-400 hover:text-red-700 hover:bg-red-50 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                                            title="Disattiva reparto"
                                            aria-label="Disattiva reparto"
                                        >
                                            <v-icon name="hi-solid-x-circle" class="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <div class="flex flex-wrap items-center gap-2 mt-4">
                                    <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-slate-50 text-xs text-slate-600 ring-1 ring-slate-100">
                                        <v-icon name="hi-solid-users" class="w-4 h-4" />
                                        {{ dept.users ? dept.users.length : 0 }} utenti
                                    </span>

                                    <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-amber-50 text-xs text-amber-700 ring-1 ring-amber-100">
                                        <v-icon name="hi-solid-star" class="w-4 h-4" />
                                        {{ dept.managers ? dept.managers.length : 0 }} manager
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="min-w-0">
                    <div
                        v-if="!selectedDept"
                        class="bg-white border border-dashed border-slate-300 rounded-2xl p-16 text-center min-h-[520px] flex flex-col items-center justify-center shadow-sm"
                    >
                        <div class="w-20 h-20 rounded-3xl bg-slate-50 text-slate-300 flex items-center justify-center">
                            <v-icon name="hi-solid-arrow-circle-left" class="w-10 h-10" />
                        </div>
                        <p class="text-slate-700 font-semibold mt-5">Seleziona un reparto</p>
                        <p class="text-slate-500 text-sm mt-1 max-w-md">
                            Da qui potrai vedere gli utenti associati, impostare i manager e modificare la composizione del reparto.
                        </p>
                    </div>

                    <div v-else class="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                        <div class="px-6 py-6 border-b border-slate-100 bg-gradient-to-br from-slate-50 via-white to-blue-50">
                            <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                                <div class="flex items-start gap-4 min-w-0">
                                    <div class="w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-700 flex items-center justify-center flex-none ring-1 ring-indigo-200">
                                        <v-icon name="bi-diagram-3" class="w-6 h-6" />
                                    </div>

                                    <div class="min-w-0">
                                        <div class="flex items-center gap-2 flex-wrap">
                                            <h2 class="text-xl font-bold text-slate-900">
                                                {{ selectedDept.name }}
                                            </h2>

                                            <span
                                                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                                                :class="selectedDept.is_active
                                                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                                                    : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200'"
                                            >
                                                {{ selectedDept.is_active ? 'Attivo' : 'Inattivo' }}
                                            </span>
                                        </div>

                                        <p class="text-sm text-slate-500 mt-1 max-w-2xl">
                                            {{ selectedDept.description || 'Nessuna descrizione inserita per questo reparto.' }}
                                        </p>

                                        <div class="flex flex-wrap items-center gap-2 mt-4">
                                            <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white text-sm text-slate-700 ring-1 ring-slate-200 shadow-sm">
                                                <v-icon name="hi-solid-users" class="w-4 h-4 text-slate-400" />
                                                {{ selectedDept.users ? selectedDept.users.length : 0 }} utenti
                                            </span>

                                            <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white text-sm text-amber-700 ring-1 ring-amber-100 shadow-sm">
                                                <v-icon name="hi-solid-star" class="w-4 h-4 text-amber-500" />
                                                {{ selectedDept.managers ? selectedDept.managers.length : 0 }} manager
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    @click="openAddUserModal"
                                    class="flex-none inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl hover:bg-blue-700 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                >
                                    <v-icon name="hi-solid-user-add" class="w-4 h-4" />
                                    Aggiungi utente
                                </button>
                            </div>
                        </div>

                        <div class="p-6">
                            <div v-if="!selectedDept.users || selectedDept.users.length === 0" class="text-center py-14">
                                <div class="w-16 h-16 rounded-2xl bg-slate-50 text-slate-300 flex items-center justify-center mx-auto">
                                    <v-icon name="hi-solid-user-remove" class="w-8 h-8" />
                                </div>
                                <p class="text-slate-700 font-semibold mt-4">Nessun utente associato</p>
                                <p class="text-slate-500 text-sm mt-1">
                                    Aggiungi almeno un utente per rendere operativo questo reparto.
                                </p>
                                <button
                                    @click="openAddUserModal"
                                    class="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-50 text-blue-700 text-sm font-semibold hover:bg-blue-100 transition-colors"
                                >
                                    <v-icon name="hi-solid-user-add" class="w-4 h-4" />
                                    Aggiungi il primo utente
                                </button>
                            </div>

                            <div v-else class="space-y-3">
                                <div class="flex items-center justify-between gap-3 mb-4">
                                    <h3 class="text-xs font-bold uppercase tracking-wide text-slate-400">
                                        Utenti associati ({{ selectedDept.users.length }})
                                    </h3>
                                </div>

                                <div
                                    v-for="user in selectedDept.users"
                                    :key="user.id"
                                    class="group flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 bg-white border border-slate-200 rounded-2xl hover:border-blue-200 hover:shadow-sm transition-all"
                                >
                                    <div class="flex items-center gap-3 min-w-0">
                                        

                                        <div class="min-w-0">
                                            <div class="flex items-center gap-2 flex-wrap">
                                                <span class="font-semibold text-slate-900 text-sm truncate">
                                                    {{ user.name }}
                                                </span>

                                                <span
                                                    v-if="user.is_manager"
                                                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 ring-1 ring-amber-100"
                                                >
                                                    <v-icon name="hi-solid-star" class="w-2.5 h-2.5 text-amber-500"></v-icon>
                                                    Manager
                                                </span>
                                            </div>

                                            <p class="text-xs text-slate-500 truncate">
                                                {{ user.email }}
                                            </p>
                                        </div>
                                    </div>

                                    <div class="flex items-center gap-2 flex-none md:ml-3">
                                        <button
                                            v-if="!user.is_manager"
                                            @click="setManager(user.id, true)"
                                            :disabled="submitting"
                                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-100 rounded-xl hover:bg-amber-100 transition-colors disabled:opacity-50"
                                        >
                                            <v-icon name="hi-solid-star" class="w-4 h-4"></v-icon>
                                            Rendi manager
                                        </button>

                                        <button
                                            v-else
                                            @click="setManager(user.id, false)"
                                            :disabled="submitting"
                                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-50 ring-1 ring-slate-200 rounded-xl hover:bg-slate-100 transition-colors disabled:opacity-50"
                                        >
                                            <v-icon name="hi-solid-star" class="w-4 h-4"></v-icon>
                                            Rimuovi manager
                                        </button>

                                        <button
                                            @click="openConfirmDetach(user)"
                                            :disabled="submitting"
                                            class="p-2 text-slate-400 hover:text-red-700 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                                            title="Rimuovi dal reparto"
                                            aria-label="Rimuovi dal reparto"
                                        >
                                            <v-icon name="hi-solid-user-remove" class="w-4 h-4"></v-icon>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="showDeptModal"
            class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
        >
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" @click.stop>
                <div class="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div class="flex items-center gap-3">
                        <span class="w-10 h-10 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center">
                            <v-icon name="bi-diagram-3" class="w-6 h-6"></v-icon>
                        </span>
                        <div>
                            <h3 class="font-bold text-slate-900">
                                {{ isEditing ? 'Modifica reparto' : 'Nuovo reparto' }}
                            </h3>
                            <p class="text-xs text-slate-500">
                                {{ isEditing ? 'Aggiorna le informazioni del reparto.' : 'Crea un nuovo reparto attività.' }}
                            </p>
                        </div>
                    </div>

                    <button
                        @click="closeDeptModal"
                        class="p-2 text-slate-400 hover:text-slate-700 hover:bg-white rounded-xl transition-colors"
                        aria-label="Chiudi modale"
                    >
                        <v-icon name="co-x" class="w-5 h-5" />
                    </button>
                </div>

                <form @submit.prevent="submitDeptForm" class="p-6 space-y-4">
                    <div>
                        <label class="block text-sm font-semibold text-slate-700 mb-1.5">
                            Nome reparto <span class="text-red-500">*</span>
                        </label>
                        <input
                            v-model="deptForm.name"
                            type="text"
                            class="p93__input w-full"
                            placeholder="es. Produzione"
                            required
                            maxlength="255"
                            autofocus
                        >
                        <p v-if="deptFormErrors.name" class="text-red-500 text-sm mt-1">
                            {{ deptFormErrors.name[0] }}
                        </p>
                    </div>

                    <div>
                        <label class="block text-sm font-semibold text-slate-700 mb-1.5">
                            Descrizione
                        </label>
                        <textarea
                            v-model="deptForm.description"
                            class="p93__input w-full"
                            rows="3"
                            placeholder="Descrizione opzionale..."
                        ></textarea>
                        <p v-if="deptFormErrors.description" class="text-red-500 text-sm mt-1">
                            {{ deptFormErrors.description[0] }}
                        </p>
                    </div>

                    <label class="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 ring-1 ring-slate-100 cursor-pointer">
                        <input
                            id="dept_is_active"
                            v-model="deptForm.is_active"
                            type="checkbox"
                            class="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                        >
                        <span>
                            <span class="block text-sm font-semibold text-slate-700">Reparto attivo</span>
                            <span class="block text-xs text-slate-500">I reparti inattivi possono essere nascosti dalle viste operative.</span>
                        </span>
                    </label>

                    <div v-if="deptFormErrors.general" class="bg-red-50 border border-red-200 rounded-2xl p-3 text-sm text-red-700">
                        {{ deptFormErrors.general }}
                    </div>

                    <div class="flex justify-end gap-3 pt-4 border-t border-slate-100">
                        <button
                            type="button"
                            @click="closeDeptModal"
                            class="px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                        >
                            Annulla
                        </button>

                        <button
                            type="submit"
                            :disabled="submitting"
                            class="px-4 py-2.5 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
                        >
                            <span v-if="submitting">Salvataggio...</span>
                            <span v-else>{{ isEditing ? 'Salva modifiche' : 'Crea reparto' }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <div
            v-if="showAddUserModal"
            class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
        >
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" @click.stop>
                <div class="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div class="flex items-center gap-3">
                        <span class="w-10 h-10 rounded-2xl bg-indigo-100 text-indigo-700 flex items-center justify-center">
                            <v-icon name="hi-solid-user-add" class="w-6 h-6"></v-icon>
                        </span>
                        <div class="min-w-0">
                            <h3 class="font-bold text-slate-900">
                                Aggiungi utente
                            </h3>
                            <p class="text-xs text-slate-500 truncate">
                                {{ selectedDept ? selectedDept.name : '' }}
                            </p>
                        </div>
                    </div>

                    <button
                        @click="closeAddUserModal"
                        class="p-2 text-slate-400 hover:text-slate-700 hover:bg-white rounded-xl transition-colors"
                        aria-label="Chiudi modale"
                    >
                        <v-icon name="co-x" class="w-5 h-5" />
                    </button>
                </div>

                <div class="p-6 space-y-4">
                    <div v-if="availableUsersLoading" class="flex flex-col items-center justify-center py-10">
                        <div class="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600"></div>
                        <p class="text-sm text-slate-500 mt-3">Caricamento utenti...</p>
                    </div>

                    <template v-else>
                        <div>
                            <label class="block text-sm font-semibold text-slate-700 mb-1.5">
                                Utente <span class="text-red-500">*</span>
                            </label>
                            <select v-model="addUserForm.user_id" class="p93__input w-full">
                                <option value="">— Seleziona utente —</option>
                                <option v-for="u in availableUsers" :key="u.id" :value="u.id">
                                    {{ u.name }} ({{ u.email }})
                                </option>
                            </select>
                            <p v-if="addUserErrors.user_id" class="text-red-500 text-sm mt-1">
                                {{ addUserErrors.user_id[0] }}
                            </p>
                        </div>

                        <label class="flex items-center gap-3 p-3 rounded-2xl bg-amber-50 ring-1 ring-amber-100 cursor-pointer">
                            <input
                                id="add_user_is_manager"
                                v-model="addUserForm.is_manager"
                                type="checkbox"
                                class="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                            >
                            <span>
                                <span class="block text-sm font-semibold text-amber-800">Manager del reparto</span>
                                <span class="block text-xs text-amber-700">Il manager potrà vedere le attività degli utenti del reparto.</span>
                            </span>
                        </label>

                        <div v-if="addUserErrors.general" class="bg-red-50 border border-red-200 rounded-2xl p-3 text-sm text-red-700">
                            {{ addUserErrors.general }}
                        </div>

                        <div class="flex justify-end gap-3 pt-4 border-t border-slate-100">
                            <button
                                type="button"
                                @click="closeAddUserModal"
                                class="px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                            >
                                Annulla
                            </button>

                            <button
                                @click="submitAddUser"
                                :disabled="submitting || !addUserForm.user_id"
                                class="px-4 py-2.5 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
                            >
                                <span v-if="submitting">Salvataggio...</span>
                                <span v-else>Aggiungi</span>
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <ConfirmationModal :show="showConfirmDeactivate" @close="showConfirmDeactivate = false">
            <template #title>Disattiva reparto</template>
            <template #content>
                Sei sicuro di voler disattivare il reparto
                <strong>{{ deptToDeactivate ? deptToDeactivate.name : '' }}</strong>?
                Il reparto sarà nascosto dalla lista ma non verrà eliminato.
                Gli utenti associati rimarranno invariati.
            </template>
            <template #footer>
                <button
                    @click="showConfirmDeactivate = false"
                    class="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 rounded-xl mr-2 transition-colors"
                >
                    Annulla
                </button>
                <button
                    @click="confirmDeactivate"
                    :disabled="submitting"
                    class="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                    Disattiva
                </button>
            </template>
        </ConfirmationModal>

        <ConfirmationModal :show="showConfirmDetach" @close="showConfirmDetach = false">
            <template #title>Rimuovi utente dal reparto</template>
            <template #content>
                Sei sicuro di voler rimuovere
                <strong>{{ userToDetach ? userToDetach.name : '' }}</strong>
                dal reparto <strong>{{ selectedDept ? selectedDept.name : '' }}</strong>?
                L'utente non verrà eliminato dal sistema.
            </template>
            <template #footer>
                <button
                    @click="showConfirmDetach = false"
                    class="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 rounded-xl mr-2 transition-colors"
                >
                    Annulla
                </button>
                <button
                    @click="confirmDetachUser"
                    :disabled="submitting"
                    class="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                    Rimuovi
                </button>
            </template>
        </ConfirmationModal>
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import ModalMessage from '@/Components/ModalMessage.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';

export default {
    components: {
        BreadCrumbs,
        ModalMessage,
        ConfirmationModal,
    },

    data() {
        return {
            departments: [],
            selectedDept: null,
            loading: true,
            loadError: null,
            accessDenied: false,
            submitting: false,
            detailLoading: false,

            showMessage: false,
            messageTitle: '',
            messageTexts: [],

            showDeptModal: false,
            isEditing: false,
            deptToEdit: null,
            deptForm: {
                name: '',
                description: '',
                is_active: true,
            },
            deptFormErrors: {},

            showAddUserModal: false,
            availableUsers: [],
            availableUsersLoading: false,
            addUserForm: {
                user_id: '',
                is_manager: false,
            },
            addUserErrors: {},

            showConfirmDeactivate: false,
            deptToDeactivate: null,

            showConfirmDetach: false,
            userToDetach: null,
        };
    },

    computed: {
        navigation() {
            return [
                { name: this.$t('ui.dashboard'), href: route('dashboard') },
                { name: this.$t('modules.attivita'), href: route('modules.home', 'attivita') },
                { name: 'Reparti', href: route('modules.attivita.reparti') },
            ];
        },

        totalDepartments() {
            return this.departments.length;
        },

        activeDepartments() {
            return this.departments.filter((department) => department.is_active).length;
        },

        totalAssignedUsers() {
            const userIds = new Set();

            this.departments.forEach((department) => {
                (department.users || []).forEach((user) => {
                    userIds.add(user.id);
                });
            });

            return userIds.size;
        },

        totalManagers() {
            const managerIds = new Set();

            this.departments.forEach((department) => {
                (department.managers || []).forEach((manager) => {
                    managerIds.add(manager.id);
                });
            });

            return managerIds.size;
        },
    },

    methods: {
        fetchDepartments(autoSelectId = null) {
            this.loading = true;
            this.loadError = null;
            this.accessDenied = false;

            axios.get(route('modules.attivita.departments.index'))
                .then((response) => {
                    this.departments = response.data;

                    if (autoSelectId) {
                        this.selectedDept = this.departments.find(d => d.id === autoSelectId) || null;
                    } else if (this.selectedDept) {
                        const refreshed = this.departments.find(d => d.id === this.selectedDept.id);
                        this.selectedDept = refreshed || null;
                    }

                    if (!this.selectedDept && this.departments.length > 0) {
                        this.selectedDept = this.departments[0];
                    }
                })
                .catch((error) => {
                    if (error.response && error.response.status === 403) {
                        this.accessDenied = true;
                    } else {
                        this.loadError = 'Errore nel caricamento dei reparti. Riprova.';
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        selectDepartment(dept) {
            this.selectedDept = dept;
        },

        openCreateModal() {
            this.isEditing = false;
            this.deptToEdit = null;
            this.deptForm = { name: '', description: '', is_active: true };
            this.deptFormErrors = {};
            this.showDeptModal = true;
        },

        openEditModal(dept) {
            this.isEditing = true;
            this.deptToEdit = dept;
            this.deptForm = {
                name: dept.name,
                description: dept.description || '',
                is_active: dept.is_active,
            };
            this.deptFormErrors = {};
            this.showDeptModal = true;
        },

        closeDeptModal() {
            this.showDeptModal = false;
            this.deptFormErrors = {};
        },

        submitDeptForm() {
            if (this.submitting) return;
            this.submitting = true;
            this.deptFormErrors = {};

            const promise = this.isEditing
                ? axios.put(route('modules.attivita.departments.update', this.deptToEdit.id), this.deptForm)
                : axios.post(route('modules.attivita.departments.store'), this.deptForm);

            promise
                .then((response) => {
                    const savedId = response.data?.id || null;
                    this.closeDeptModal();
                    this.fetchDepartments(savedId);
                    this.showNotification(
                        'Operazione completata',
                        [this.isEditing ? 'Reparto aggiornato.' : 'Reparto creato con successo.']
                    );
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.deptFormErrors = error.response.data.errors || {};
                        if (!Object.keys(this.deptFormErrors).length) {
                            this.deptFormErrors.general = error.response.data.message || 'Dati non validi.';
                        }
                    } else {
                        this.deptFormErrors.general = 'Errore imprevisto. Riprova.';
                    }
                })
                .finally(() => {
                    this.submitting = false;
                });
        },

        openConfirmDeactivate(dept) {
            this.deptToDeactivate = dept;
            this.showConfirmDeactivate = true;
        },

        confirmDeactivate() {
            if (this.submitting || !this.deptToDeactivate) return;
            this.submitting = true;

            axios.delete(route('modules.attivita.departments.destroy', this.deptToDeactivate.id))
                .then(() => {
                    this.showConfirmDeactivate = false;
                    if (this.selectedDept && this.selectedDept.id === this.deptToDeactivate.id) {
                        this.selectedDept = null;
                    }
                    this.deptToDeactivate = null;
                    this.fetchDepartments();
                    this.showNotification('Operazione completata', ['Reparto disattivato.']);
                })
                .catch(() => {
                    this.showNotification('Errore', ['Impossibile disattivare il reparto. Riprova.']);
                })
                .finally(() => {
                    this.submitting = false;
                });
        },

        openAddUserModal() {
            if (!this.selectedDept) return;
            this.addUserForm = { user_id: '', is_manager: false };
            this.addUserErrors = {};
            this.showAddUserModal = true;
            this.fetchAvailableUsers();
        },

        closeAddUserModal() {
            this.showAddUserModal = false;
            this.addUserErrors = {};
        },

        fetchAvailableUsers() {
            this.availableUsersLoading = true;

            axios.get(route('modules.attivita.departments.users.available'))
                .then((response) => {
                    this.availableUsers = response.data;
                })
                .catch(() => {
                    this.addUserErrors.general = 'Errore nel caricamento degli utenti.';
                })
                .finally(() => {
                    this.availableUsersLoading = false;
                });
        },

        submitAddUser() {
            if (this.submitting || !this.addUserForm.user_id || !this.selectedDept) return;
            this.submitting = true;
            this.addUserErrors = {};

            axios.post(
                route('modules.attivita.departments.users.attach', this.selectedDept.id),
                { user_id: this.addUserForm.user_id, is_manager: this.addUserForm.is_manager }
            )
                .then((response) => {
                    this.closeAddUserModal();
                    this.selectedDept = response.data;
                    this.fetchDepartments(this.selectedDept.id);
                    this.showNotification('Operazione completata', ['Utente aggiunto al reparto.']);
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.addUserErrors = error.response.data.errors || {};
                        if (!Object.keys(this.addUserErrors).length) {
                            this.addUserErrors.general = error.response.data.message || 'Dati non validi.';
                        }
                    } else {
                        this.addUserErrors.general = 'Errore imprevisto. Riprova.';
                    }
                })
                .finally(() => {
                    this.submitting = false;
                });
        },

        setManager(userId, isManager) {
            if (this.submitting || !this.selectedDept) return;
            this.submitting = true;

            axios.patch(
                route('modules.attivita.departments.users.manager', {
                    department: this.selectedDept.id,
                    user: userId,
                }),
                { is_manager: isManager }
            )
                .then((response) => {
                    this.selectedDept = response.data;
                    this.fetchDepartments(this.selectedDept.id);
                    this.showNotification(
                        'Operazione completata',
                        [isManager ? 'Utente impostato come manager.' : 'Ruolo manager rimosso.']
                    );
                })
                .catch(() => {
                    this.showNotification('Errore', ["Impossibile aggiornare il ruolo. Riprova."]);
                })
                .finally(() => {
                    this.submitting = false;
                });
        },

        openConfirmDetach(user) {
            this.userToDetach = user;
            this.showConfirmDetach = true;
        },

        confirmDetachUser() {
            if (this.submitting || !this.userToDetach || !this.selectedDept) return;
            this.submitting = true;

            axios.delete(
                route('modules.attivita.departments.users.detach', {
                    department: this.selectedDept.id,
                    user: this.userToDetach.id,
                })
            )
                .then(() => {
                    this.showConfirmDetach = false;
                    const removedId = this.userToDetach.id;
                    this.userToDetach = null;
                    this.selectedDept = {
                        ...this.selectedDept,
                        users: (this.selectedDept.users || []).filter(u => u.id !== removedId),
                        managers: (this.selectedDept.managers || []).filter(u => u.id !== removedId),
                    };
                    this.fetchDepartments(this.selectedDept.id);
                    this.showNotification('Operazione completata', ['Utente rimosso dal reparto.']);
                })
                .catch(() => {
                    this.showNotification('Errore', ['Impossibile rimuovere l\'utente. Riprova.']);
                })
                .finally(() => {
                    this.submitting = false;
                });
        },

        showNotification(title, messages) {
            this.messageTitle = title;
            this.messageTexts = messages;
            this.showMessage = true;
        },
    },

    mounted() {
        this.fetchDepartments();
    },
};
</script>